import { Stack } from "@mui/material";
import React, { ReactElement, useMemo, useState } from "react";
import { FieldPath, FieldValues, Path, useFormContext } from "react-hook-form";

import FormSection from "./FormSection";
import Text from "../atoms/Text";
import TextAreaField from "../atoms/TextAreaField";
import TextField from "../atoms/TextField";
import ToggleGroup from "../atoms/ToggleGroup";

type Toggles<TFieldValue extends FieldValues> = FieldPath<TFieldValue>[];

export type Option<TFieldValues extends FieldValues> = {
  label: string;
  value: Path<TFieldValues>;
};

type Toggle<TFieldValues extends FieldValues> = Option<TFieldValues>;

export type ToggleOptions<TFieldValues extends FieldValues> =
  Toggle<TFieldValues>[];

type LanguageFormSectionProps<TFieldValues extends FieldValues> = {
  sectionTitle: string;
  requiredValue?: string;
  options: ToggleOptions<TFieldValues>;
};

const LanguageFormSection = <TFieldValues extends FieldValues>({
  sectionTitle,
  requiredValue,
  options,
}: LanguageFormSectionProps<TFieldValues>) => {
  const { unregister, resetField, getValues } = useFormContext<TFieldValues>();

  const initialToggles = useMemo(() => {
    const initialToggles = options.reduce(
      (acc: Toggles<TFieldValues>, option) => {
        const value = option.value as FieldPath<TFieldValues>;
        if (getValues(value)) {
          acc.push(value);
        }
        return acc;
      },
      [],
    );
    // Add mandatory field if exists
    if (
      !!requiredValue &&
      !initialToggles.includes(requiredValue as FieldPath<TFieldValues>)
    ) {
      initialToggles.push(requiredValue as FieldPath<TFieldValues>);
    }
    return initialToggles;
  }, []);

  const [toggles, setToggles] = useState<Toggles<TFieldValues>>(initialToggles);

  const handleToggle = (
    e: React.MouseEvent<HTMLElement>,
    newToggles: Toggles<TFieldValues>,
  ) => {
    // do not unselect the mandatory field if exists
    if (
      !!requiredValue &&
      !newToggles.includes(requiredValue as FieldPath<TFieldValues>)
    ) {
      newToggles.push(requiredValue as FieldPath<TFieldValues>);
    }
    if (toggles.length > newToggles.length) {
      const removedItem = toggles.find(item => !newToggles.includes(item));
      if (removedItem) {
        resetField(removedItem);
        unregister(removedItem);
      }
    }
    setToggles(newToggles);
  };

  return (
    <Stack p={4} spacing={2} bgcolor="mono.white" borderRadius={4}>
      <Text variant="title_small">{sectionTitle}</Text>
      <ToggleGroup value={toggles} options={options} onChange={handleToggle} />
      {options.reduce((stacks: ReactElement[], option) => {
        if (toggles.includes(option.value)) {
          stacks.push(
            <FormSection
              key={option.value}
              sectionTitle={option.label}
              customStyles={{ bgcolor: "background.default", p: 2 }}
              direction="row"
            >
              <>
                <Stack spacing={2} width={"-webkit-fill-available"}>
                  <TextField
                    name={`${option.value}.displayName`}
                    label="Display Name"
                  />
                  <TextField
                    name={`${option.value}.bannerHeadline`}
                    label="Banner Headline"
                  />
                  <TextAreaField
                    name={`${option.value}.productDescription`}
                    label="Product Description"
                    rows={3.5}
                  />
                  <TextAreaField
                    name={`${option.value}.termsAndConditions`}
                    label="Terms and Conditions"
                    rows={10.5}
                  />
                </Stack>
                <Stack spacing={2} width={"-webkit-fill-available"}>
                  <TextField
                    name={`${option.value}.redemptionInstructionHeader`}
                    label="Redemption Header"
                  />
                  <TextAreaField
                    name={`${option.value}.redemptionInstructions`}
                    label="Redemption Instructions"
                    rows={10.5}
                  />
                </Stack>
              </>
            </FormSection>,
          );
        }
        return stacks;
      }, [])}
    </Stack>
  );
};

export default LanguageFormSection;
